import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  AddDeviceForHealthProfessional,
  ConfirmDeviceReassignment,
  ConfirmDeviceRemoval,
  DeleteDeviceByID,
  GetAllDevice,
  GetDeviceById,
  UpdateDeviceByID,
} from "../../../utils/apiCalls/devices";
import { GetAllHealthProfessionalIds } from "../../../utils/apiCalls/healthProfessionals";
import { useToast } from "../../../utils/ToastContext";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import useConfirmDialog from "../../../hooks/useConfirmDialog";

const DEVICE_NAME_PREFIX = "We-Be ";
// Same GS1 UDI carton prefix + (10)/(21) segment layout as the Registration
// flow (see Auth/Registration/useRegistration.js) - kept in sync manually
// since both pages send this string to the same backend field.
export const UDI_STATIC_PREFIX = "(01)00860016217508";
const UDI_LOT_CODE_RE = /^[A-Za-z0-9]{3}$/;
const UDI_SERIAL_RE = /^\d{7}$/;

const initialValues = {
  deviceName: "",
  udiLotCode: "",
  udiSerial: "",
  numberOfSlots: "",
  healthProfessionalId: "",
  healthProfessionalName: "",
};

// Reverses the `${UDI_STATIC_PREFIX}(10)<lotCode>(21)<serial>` string the
// backend stores back into the two fields the form edits.
const parseDeviceUdi = (deviceUdi) => {
  const match = (deviceUdi || "").match(/\(10\)([^(]*)\(21\)(.*)$/);
  return {
    udiLotCode: match?.[1] || "",
    udiSerial: match?.[2] || "",
  };
};

const useAddDevice = () => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const uploadJsonDialog = useConfirmDialog();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const deleteDialog = useConfirmDialog();
  const reassignDialog = useConfirmDialog();
  // Holds the in-flight update's FormData while reassignDialog is open, so
  // confirming can resubmit the exact same edit once the old HP is cleared.
  const pendingUpdateFormDataRef = useRef(null);
  const [addForm, setAddForm] = useState(initialValues);
  const [errors, setErrors] = useState(initialValues);
  const [createMode, setCreateMode] = useState("manual");
  const [jsonFile, setJsonFile] = useState(null);
  const [jsonFileError, setJsonFileError] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [fetchError, setFetchError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 500);
  const [addOrEditDevice, setAddOrEditDevice] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const editingDeviceIdRef = useRef(null);
  // The device's UDI exactly as fetched, kept aside for submit: a UDI that
  // predates the (10)/(21) split (or was set via JSON upload) won't match
  // parseDeviceUdi, so the lot/serial boxes stay blank - without this,
  // saving any other field on that device would wipe its UDI to "".
  const originalDeviceUdiRef = useRef("");
  const [healthProfessionalData, setHealthProfessionalData] = useState([]);

  const openAddModal = () => {
    setIsEditing(false);
    editingDeviceIdRef.current = null;
    originalDeviceUdiRef.current = "";
    setAddForm(initialValues);
    setErrors(initialValues);
    setCreateMode("manual");
    setJsonFile(null);
    setJsonFileError("");
    setAddOrEditDevice(true);
  };

  const openEditModal = async (row) => {
    setIsEditing(true);
    editingDeviceIdRef.current = row.deviceId;
    setErrors(initialValues);

    // Fetch the full record instead of trusting the list row, same reason
    // as the HP devices page: the list can be stale/incomplete, so patch
    // the form from GetDeviceById.
    setIsSubmitting(true);
    try {
      const res = await GetDeviceById(row.deviceId);
      const deviceData = res?.statusCode === 200 ? res.data : row;
      originalDeviceUdiRef.current = deviceData.deviceUdi || "";
      setAddForm({
        deviceName: deviceData.deviceName ?? row.deviceName ?? "",
        ...parseDeviceUdi(deviceData.deviceUdi),
        numberOfSlots: deviceData.numberOfSlots ?? row.numberOfSlots ?? "",
        healthProfessionalId: deviceData.healthProfessionalId ?? row.healthProfessionalId ?? null,
        healthProfessionalName:
          deviceData.healthProfessionalName ?? row.healthProfessionalName ?? "Not Assigned",
      });
    } catch (error) {
      console.error("Error fetching device details:", error?.message);
      originalDeviceUdiRef.current = row.deviceUdi || "";
      setAddForm({
        deviceName: row.deviceName ?? "",
        ...parseDeviceUdi(row.deviceUdi),
        numberOfSlots: row.numberOfSlots ?? "",
        healthProfessionalId: row.healthProfessionalId ?? null,
        healthProfessionalName: row.healthProfessionalName ?? "Not Assigned",
      });
    } finally {
      setIsSubmitting(false);
      setAddOrEditDevice(true);
    }
  };

  const closeAddEditModal = () => {
    setAddOrEditDevice(false);
    setAddForm(initialValues);
    setErrors(initialValues);
    setCreateMode("manual");
    setJsonFile(null);
    setJsonFileError("");
  };

  const handleJsonFileSelected = (file, error) => {
    setJsonFile(file);
    setJsonFileError(error || "");
  };

  const handleChange = (eOrValue) => {
    // Case 1: normal input event
    if (eOrValue?.target) {
      const { name, value } = eOrValue.target;
      setAddForm((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }

    // Case 2: dropdown gives { name, value } OR (name, value)
    const { name, value } = eOrValue;

    setAddForm((prev) => {
      // if changing HP, also patch selected name (optional)
      if (name === "healthProfessionalId") {
        const selected = healthProfessionalData.find((x) => x.value === value);
        return {
          ...prev,
          healthProfessionalId: value,
          healthProfessionalName: selected?.healthProfessionalName,
        };
      }
      return { ...prev, [name]: value };
    });

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const isJsonCreate = !isEditing && createMode === "json";

  // replace validateField with this
  const validateField = () => {
    const errObj = { ...initialValues };
    let jsonErr = "";

    if (isJsonCreate) {
      if (!jsonFile) jsonErr = "Please select a JSON file";
    } else if (!isEditing) {
      // deviceName (4-digit code) is required only when adding manually
      if (!addForm.deviceName) {
        errObj.deviceName = "This field is required";
      } else if (addForm.deviceName.length !== 4) {
        errObj.deviceName = "Enter a 4 digit code";
      }
    }

    // numberOfSlots is required for manual add; optional when editing or
    // creating via JSON upload (server defaults/keeps the current value).
    const numberOfSlotsRequired = !isEditing && createMode === "manual";
    const numberOfSlotsProvided = addForm.numberOfSlots || addForm.numberOfSlots === 0;
    if (numberOfSlotsRequired && !numberOfSlotsProvided) {
      errObj.numberOfSlots = "This field is required";
    } else if (numberOfSlotsProvided) {
      if (!Number.isInteger(Number(addForm.numberOfSlots))) {
        errObj.numberOfSlots = "Enter a whole number";
      } else if (Number(addForm.numberOfSlots) < 0) {
        errObj.numberOfSlots = "Enter a valid non-negative number";
      }
    }

    // Device UDI doesn't apply to JSON-upload creation (derived from the file).
    if (!isJsonCreate && (addForm.udiLotCode || addForm.udiSerial)) {
      if (!UDI_LOT_CODE_RE.test(addForm.udiLotCode)) {
        errObj.udiLotCode = "Enter a 3-character alphanumeric code";
      }
      if (!UDI_SERIAL_RE.test(addForm.udiSerial)) {
        errObj.udiSerial = "Enter a 7-digit serial";
      }
    }

    setErrors(errObj);
    setJsonFileError(jsonErr);
    return Object.values(errObj).every((x) => !x) && !jsonErr;
  };

  const getAllHealthProfessionalData = async () => {
    try {
      const res = await GetAllHealthProfessionalIds();
      const { data, statusCode } = res;
      if (statusCode === 200) {
        const formattedData = data.map((item) => ({
          value: item.healthProfessionalId,
          label: `${item.healthProfessionalName} - ${item.email}`,
          healthProfessionalName: item.healthProfessionalName,
        }));
        setHealthProfessionalData(formattedData);
      }
    } catch (error) {
      console.error("Error fetching health professional ids:", error?.message);
    }
  };

  useEffect(() => {
    getAllHealthProfessionalData();
  }, []);
  // update handleSubmit
  const handleSubmit = async () => {
    setIsSubmitting(true);
    if (!validateField()) {
      setIsSubmitting(false);
      return;
    }

    const deviceUdi =
      addForm.udiLotCode && addForm.udiSerial
        ? `${UDI_STATIC_PREFIX}(10)${addForm.udiLotCode}(21)${addForm.udiSerial}`
        // Leaving both boxes blank while editing means "didn't touch the
        // UDI" (they may not even show, per the comment on
        // originalDeviceUdiRef above) - keep the device's existing value
        // instead of wiping it.
        : isEditing
          ? originalDeviceUdiRef.current
          : "";

    if (isEditing) {
      // UPDATE: deviceId + deviceUdi + healthProfessionalId are always sent;
      // numberOfSlots is only sent when provided (omitting it leaves the
      // device's current value untouched).
      const updateFormData = new FormData();
      updateFormData.append("deviceId", editingDeviceIdRef.current);
      updateFormData.append("deviceUdi", deviceUdi);
      updateFormData.append(
        "healthProfessionalId",
        addForm.healthProfessionalId ? Number(addForm.healthProfessionalId) : "",
      );
      if (addForm.numberOfSlots || addForm.numberOfSlots === 0) {
        updateFormData.append("numberOfSlots", Number(addForm.numberOfSlots));
      }

      // A rejected update can mean this device already has active patients
      // tied to its current HP - the API flags that case via `data.deviceId`
      // / `data.healthProfessionalId`, so ask the admin to confirm the
      // reassignment instead of just surfacing the error.
      try {
        const res = await UpdateDeviceByID(updateFormData);
        const { statusCode, message, data } = res;
        if (statusCode === 200) {
          showToast("Device updated successfully", "success");
          fetchAllDevices(page, rowsPerPage, debouncedSearchQuery);
          closeAddEditModal();
        } else if (data?.deviceId && data?.healthProfessionalId) {
          pendingUpdateFormDataRef.current = updateFormData;
          reassignDialog.openDialog(data);
        } else {
          showToast(message || "Failed to update device", "error");
        }
      } catch (error) {
        showToast(error?.message || "Failed to update device", "error");
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // ADD: either the manual fields, or a jsonFile the backend derives
      // deviceName/deviceUdi from. numberOfSlots and healthProfessionalId
      // are optional in both modes - omitted means "default to 0" /
      // "unassigned" server-side, no error.
      const addFormData = new FormData();
      if (createMode === "json") {
        addFormData.append("jsonFile", jsonFile);
      } else {
        addFormData.append("deviceName", `${DEVICE_NAME_PREFIX}${addForm.deviceName}`);
        if (deviceUdi) addFormData.append("deviceUdi", deviceUdi);
      }
      if (addForm.numberOfSlots || addForm.numberOfSlots === 0) {
        addFormData.append("numberOfSlots", Number(addForm.numberOfSlots));
      }
      if (addForm.healthProfessionalId) {
        addFormData.append("healthProfessionalId", Number(addForm.healthProfessionalId));
      }

      try {
        const res = await AddDeviceForHealthProfessional(addFormData);
        const { statusCode, message } = res;
        if (statusCode === 200) {
          showToast("Data Added Successfully", "success");
          fetchAllDevices(page, rowsPerPage, debouncedSearchQuery);
          closeAddEditModal();
        } else {
          showToast(message || "Failed to add device", "error");
        }
      } catch (error) {
        showToast(error?.message || "Failed to add device", "error");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const fetchAllDevices = useCallback(
    async (pageIndex, pageSize, searchString) => {
      setIsLoading(true);
      setFetchError("");
      const payload = {
        pageIndex: pageIndex,
        pageSize: pageSize,
        searchString: searchString ?? "",
      };
      try {
        const response = await GetAllDevice(payload);
        if (response.statusCode === 200) {
          setRows(response.data);
          setTotalRows(response.totalRecords);
        } else {
          console.error(
            "Error fetching professional details:",
            response.message,
          );
          setRows([]);
          setFetchError(response.message || "Failed to load devices");
        }
      } catch (error) {
        console.error("Error fetching professional details:", error?.message);
        setRows([]);
        setFetchError(error?.message || "Failed to load devices");
      }
      setIsLoading(false);
    },
    [],
  );

  useEffect(() => {
    fetchAllDevices(page, rowsPerPage, debouncedSearchQuery);
  }, [fetchAllDevices, debouncedSearchQuery, page, rowsPerPage]);

  const retryFetchDevices = () => {
    fetchAllDevices(page, rowsPerPage, debouncedSearchQuery);
  };

  const handleDelete = (row) => deleteDialog.openDialog(row);

  const handleUploadJson = (row) => uploadJsonDialog.openDialog(row);

  const handleViewDevice = (row) => {
    navigate(`/device-data/${row.deviceId}`, { state: { row } });
  };

  const confirmDelete = async () => {
    const row = deleteDialog.target;
    setIsSubmitting(true);
    try {
      if (row.userName && row.healthProfessionalId) {
        const removalRes = await ConfirmDeviceRemoval({
          healthProfessionalId: Number(row.healthProfessionalId),
          deviceIds: [Number(row.deviceId)],
        });
        if (removalRes.statusCode !== 200) {
          showToast(
            removalRes.message || "Failed to unassign device",
            "error",
          );
          return;
        }
      }

      const res = await DeleteDeviceByID(row.deviceId);
      const { statusCode, message } = res;
      if (statusCode === 200) {
        showToast(message, "success");
        fetchAllDevices(page, rowsPerPage, searchQuery);
      } else {
        showToast(message, "error");
      }
    } catch (error) {
      showToast(error?.message, "error");
    } finally {
      setIsSubmitting(false);
      deleteDialog.closeDialog();
    }
  };

  // Don't clear pendingUpdateFormDataRef here: ConfirmDialog calls onClose
  // (this) *before* onButtonClick (confirmReassignment), so clearing it would
  // wipe the form data confirm needs. The ref is overwritten on every conflict
  // and cleared in confirmReassignment's finally.
  const cancelReassignment = () => reassignDialog.closeDialog();

  const confirmReassignment = async () => {
    const { deviceId, healthProfessionalId } = reassignDialog.target || {};
    const updateFormData = pendingUpdateFormDataRef.current;
    if (!deviceId || !healthProfessionalId || !updateFormData) {
      cancelReassignment();
      return;
    }

    setIsSubmitting(true);
    try {
      const removalRes = await ConfirmDeviceReassignment({
        deviceId: Number(deviceId),
        healthProfessionalId: Number(healthProfessionalId),
      });
      if (removalRes.statusCode !== 200) {
        showToast(
          removalRes.message || "Failed to remove device from current Health Professional",
          "error",
        );
        return;
      }

      const res = await UpdateDeviceByID(updateFormData);
      if (res.statusCode === 200) {
        showToast("Device updated successfully", "success");
        fetchAllDevices(page, rowsPerPage, debouncedSearchQuery);
        closeAddEditModal();
      } else {
        showToast(res.message || "Failed to update device", "error");
      }
    } catch (error) {
      showToast(error?.message || "Failed to update device", "error");
    } finally {
      setIsSubmitting(false);
      pendingUpdateFormDataRef.current = null;
      reassignDialog.closeDialog();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  return {
    rows,
    page,
    rowsPerPage,
    isLoading,
    isSubmitting,
    deleteDialog,
    reassignDialog,
    confirmReassignment,
    cancelReassignment,
    addForm,
    errors,
    createMode,
    setCreateMode,
    jsonFile,
    jsonFileError,
    handleJsonFileSelected,
    totalRows,
    fetchError,
    searchQuery,
    addOrEditDevice,
    isEditing,
    healthProfessionalData,
    openAddModal,
    openEditModal,
    closeAddEditModal,
    handleChange,
    handleSubmit,
    retryFetchDevices,
    handleDelete,
    confirmDelete,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearchChange,
    uploadJsonDialog,
    handleUploadJson,
    handleViewDevice,
  };
};

export default useAddDevice;
